<template>
  <div>
    <!-- Buff list / management dialog -->
    <v-dialog v-model="dialog" max-width="80%">
      <v-card class="buff-management-card" style="background-color:#DEB887">
        <v-card-title class="headline" style="margin-bottom:8px">
          <div>Edit Buffs</div>
          <v-spacer/>
          <v-btn class="mr-2" @click="commonDialog = true">Add Common Buffs</v-btn>
          <v-btn class="mr-2" @click="openAddTabDialog()">Add Tab</v-btn>
          <v-btn @click="openAddBuff()">Add</v-btn>
        </v-card-title>
        <v-card-text class="buff-management-content">
          <div class="buff-management-tabs">
            <v-tabs
              ref="buffTabs"
              v-model="editBuffTab"
              background-color="#DEB887"
              color="#3e2723"
              show-arrows
            >
              <v-tab
                v-for="tab in allTabs"
                :key="tab"
                :data-buff-tab="tab"
                :href="`#${tab}`"
                class="buff-management-tab"
                :class="{ 'buff-edit-tab--drop': dragOverTab === tab }"
              >
                <span>{{ tabLabel(tab) }}</span>
                <v-icon
                  v-if="tab !== mainTab"
                  small
                  class="ml-1 buff-tab-drag-handle"
                  style="opacity:0.85; cursor:grab;"
                  title="Drag to reorder tab"
                  @click.stop
                >drag_indicator</v-icon>
                <v-icon
                  v-if="tab !== mainTab"
                  small
                  class="ml-1"
                  style="opacity:0.85;"
                  title="Rename tab"
                  @click.stop="openRenameTabDialog(tab)"
                >edit</v-icon>
                <v-icon
                  v-if="tab !== mainTab"
                  small
                  class="ml-1"
                  style="opacity:0.85;"
                  title="Delete tab (buffs move to Main)"
                  @click.stop="openDeleteTabDialog(tab)"
                >close</v-icon>
              </v-tab>
            </v-tabs>
          </div>

          <div v-if="currentTabBuffs.length === 0" class="text-subtitle-1" style="color:black; text-align:center; padding-bottom:16px;">
            No buffs on this tab. Press Add to create one, or drag a buff onto this tab.
          </div>
          <draggable
            :list="currentTabBuffs"
            :group="{ name: 'buff', put: false, pull: false }"
            handle=".drag-handle"
            @start="onBuffDragStart"
            @end="onBuffDragEnd"
          >
            <v-row
              v-for="buff in currentTabBuffs"
              :key="buff.id"
              style="display:flex; align-items:center; margin-bottom:16px; border-style:solid; border-color:grey; border-width:2px;"
              class="mx-2 pt-2 pb-2"
            >
              <v-icon style="margin-top:8px" class="mx-2 drag-handle">drag_handle</v-icon>
              <v-checkbox
                v-model="buff.active"
                hide-details
                dense
                class="mx-2 mt-2"
                title="Active"
                @change="onBuffToggled(buff)"
              ></v-checkbox>
              <div class="mx-2" style="flex:1; color:black;">
                <div style="font-weight:bold; font-size:16px;">
                  {{ buff.name || 'Unnamed Buff' }}
                  <span v-if="buff.remainingRounds != null" style="font-weight:normal; font-size:13px; color:#5d4037;">({{ formatDuration(buff.remainingRounds) }} left)</span>
                </div>
                <div style="font-size:13px;">
                  <span v-for="(effect, index) in buff.effects" :key="effect.id">
                    {{ describeEffect(effect) }}<span v-if="index < buff.effects.length - 1">, </span>
                  </span>
                </div>
              </div>
              <v-icon @click="openEditBuff(buff)" style="margin-top:12px" class="mx-2">edit</v-icon>
              <v-icon @click="removeBuff(buff)" style="margin-top:12px" class="mx-2">delete_forever</v-icon>
            </v-row>
          </draggable>
          <div style="font-size:12px; color:#5d4037; margin-top:4px;">
            Drag a buff onto another tab to move it. Use the handle to reorder within a tab.
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="tabNameDialog" max-width="420px">
      <v-card style="background-color:#DEB887">
        <v-card-title class="headline">{{ tabNameMode === 'add' ? 'Add Buff Tab' : 'Rename Buff Tab' }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="tabNameInput"
            label="Tab name"
            autofocus
            :error-messages="tabNameError"
            @keyup.enter="saveTabName()"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="closeTabNameDialog()">Cancel</v-btn>
          <v-btn color="primary" @click="saveTabName()">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteTabDialog" max-width="420px">
      <v-card style="background-color:#DEB887">
        <v-card-title class="headline">Delete Buff Tab?</v-card-title>
        <v-card-text style="color:black;">
          Buffs in <strong>{{ tabLabel(tabPendingDeletion) }}</strong> will be moved to Main.
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="deleteTabDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="confirmDeleteTab()">Delete Tab</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Common buffs picker: builds normal buffs scaled to a caster level -->
    <v-dialog v-model="commonDialog" max-width="700px">
      <v-card style="background-color:#DEB887">
        <v-card-title class="headline">
          <div>Add Common Buffs</div>
          <v-spacer/>
          <v-text-field
            v-model="casterLevel"
            label="Caster Level"
            type="number"
            min="1"
            hide-details
            dense
            style="max-width:120px;"
          ></v-text-field>
        </v-card-title>
        <v-card-text style="max-height:65vh; overflow-y:auto;">
          <v-row
            v-for="definition in commonBuffDefinitions"
            :key="definition.name"
            no-gutters
            style="align-items:center; border-bottom:1px solid grey; padding:6px 4px;"
          >
            <div style="flex:1; color:black;">
              <div style="font-weight:bold; font-size:15px;">
                {{ definition.name }}
                <span style="font-weight:normal; font-size:12px; color:#5d4037;">({{ commonBuffDurationText(definition) }})</span>
              </div>
              <div style="font-size:13px;">{{ commonBuffPreview(definition) }}</div>
            </div>
            <v-btn small class="ml-2" @click="addCommonBuff(definition)">Add</v-btn>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <div v-if="commonAddedMessage" style="color:#1b5e20; font-weight:bold; margin-left:8px;">{{ commonAddedMessage }}</div>
          <v-spacer/>
          <v-btn text @click="commonDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add / edit buff dialog -->
    <v-dialog v-model="editDialog" max-width="900px" persistent>
      <v-card style="background-color:#DEB887">
        <v-card-title class="headline">
          <div>{{ editingBuffId === null ? 'Add Buff' : 'Edit Buff' }}</div>
        </v-card-title>
        <v-card-text v-if="workingBuff">
          <v-row class="mb-2">
            <v-col cols="6">
              <v-text-field
                v-model="workingBuff.name"
                label="Buff Name"
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="3">
              <v-text-field
                v-model="workingBuff.durationValue"
                label="Duration (optional)"
                type="number"
                min="1"
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="3">
              <v-select
                v-model="workingBuff.durationUnit"
                :items="durationUnits"
                label="Unit"
                hide-details
              ></v-select>
            </v-col>
          </v-row>

          <div class="d-flex align-center mb-2">
            <div class="text-subtitle-1" style="color:black; font-weight:bold;">Bonuses</div>
            <v-icon class="ml-2" @click="addEffect()" title="Add another bonus">add_circle</v-icon>
          </div>

          <v-row
            v-for="effect in workingBuff.effects"
            :key="effect.id"
            style="align-items:center; border-style:solid; border-color:grey; border-width:1px; margin-bottom:8px;"
            class="mx-0 pt-1 pb-1"
          >
            <v-col cols="2">
              <v-text-field
                v-model="effect.amount"
                label="Amount"
                :placeholder="amountPlaceholder(effect)"
                hide-details
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="3">
              <v-select
                v-model="effect.category"
                :items="categories"
                label="Category"
                hide-details
                dense
                @change="onCategoryChange(effect)"
              ></v-select>
            </v-col>
            <v-col cols="3">
              <v-select
                v-model="effect.target"
                :items="targetOptions(effect.category)"
                label="Target"
                hide-details
                dense
                :disabled="!effect.category"
                :menu-props="{ maxHeight: 300, offsetY: true, contentClass: 'buff-select-menu' }"
                @change="onTargetChange(effect)"
              ></v-select>
            </v-col>
            <v-col cols="3">
              <v-select
                v-model="effect.bonusType"
                :items="bonusTypeOptions(effect.category, effect.target)"
                label="Bonus Type"
                hide-details
                dense
                :disabled="!effect.target"
                :menu-props="{ maxHeight: 300, offsetY: true, contentClass: 'buff-select-menu' }"
              ></v-select>
            </v-col>
            <v-col cols="1" style="text-align:center;">
              <v-icon
                :disabled="workingBuff.effects.length <= 1"
                @click="removeEffect(effect)"
              >delete_forever</v-icon>
            </v-col>
          </v-row>

          <div v-if="editError" style="color:#b71c1c; font-weight:bold; margin-top:8px;">{{ editError }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="cancelEdit()">Cancel</v-btn>
          <v-btn color="primary" @click="confirmEdit()">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import Sortable from 'sortablejs';
import buffUtilities from './buffUtilities.js';
import commonBuffs from './commonBuffs.js';

export default {
  name: 'BuffManagementDialog',
  components: {
    draggable,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    characterData: {
      type: Object,
      required: true,
    },
    pointTrackers: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      editDialog: false,
      editingBuffId: null,
      workingBuff: null,
      editError: '',
      commonDialog: false,
      casterLevel: 1,
      commonAddedMessage: '',
      commonAddedTimer: null,
      editBuffTab: buffUtilities.MAIN_BUFF_TAB,
      mainTab: buffUtilities.MAIN_BUFF_TAB,
      currentTabBuffs: [],
      draggingBuff: null,
      dragOverTab: null,
      syncingTabList: false,
      tabNameDialog: false,
      tabNameMode: 'add',
      tabNameOriginal: null,
      tabNameInput: '',
      tabNameError: '',
      deleteTabDialog: false,
      tabPendingDeletion: null,
      tabSortable: null,
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    buffs() {
      return this.characterData.buffs || [];
    },
    buffTabs() {
      return this.characterData.buffTabs || [];
    },
    allTabs() {
      return [this.mainTab, ...this.buffTabs];
    },
    categories() {
      return buffUtilities.CATEGORIES;
    },
    durationUnits() {
      return buffUtilities.DURATION_UNITS.map(unit => ({ value: unit.value, text: unit.text }));
    },
    commonBuffDefinitions() {
      return commonBuffs.COMMON_BUFFS;
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.ensureBuffStructure();
        if (!this.allTabs.includes(this.editBuffTab)) this.editBuffTab = this.mainTab;
        this.syncCurrentTabBuffs();
        this.$nextTick(() => this.setupTabSortable());
      }
    },
    editBuffTab() {
      this.syncCurrentTabBuffs();
    },
    buffs: {
      deep: true,
      handler() {
        if (!this.syncingTabList) this.syncCurrentTabBuffs();
      },
    },
  },
  beforeDestroy() {
    this.destroyTabSortable();
    window.removeEventListener('dragover', this.onGlobalDragOver);
    window.removeEventListener('mousemove', this.onGlobalDragOver);
    if (this.commonAddedTimer) clearTimeout(this.commonAddedTimer);
  },
  methods: {
    ensureBuffStructure() {
      if (!Array.isArray(this.characterData.buffTabs)) {
        this.$set(this.characterData, 'buffTabs', []);
      }
      if (!Array.isArray(this.characterData.buffs)) {
        this.$set(this.characterData, 'buffs', []);
      }
      const known = new Set([this.mainTab, ...this.characterData.buffTabs]);
      this.characterData.buffs.forEach(buff => {
        if (!buff) return;
        if (!buff.tab) this.$set(buff, 'tab', this.mainTab);
        if (buff.tab !== this.mainTab && !known.has(buff.tab)) {
          this.characterData.buffTabs.push(buff.tab);
          known.add(buff.tab);
        }
      });
    },
    ensureBuffsArray() {
      this.ensureBuffStructure();
    },
    tabLabel(tab) {
      return tab === this.mainTab ? 'Main' : tab;
    },
    setupTabSortable() {
      this.destroyTabSortable();
      const tabs = this.$refs.buffTabs;
      const tabContent = tabs && tabs.$el.querySelector('.v-slide-group__content');
      if (!tabContent) return;
      this.tabSortable = Sortable.create(tabContent, {
        animation: 150,
        draggable: '.buff-management-tab',
        handle: '.buff-tab-drag-handle',
        ghostClass: 'buff-tab-sort-ghost',
        chosenClass: 'buff-tab-sort-chosen',
        onMove: event => event.related.getAttribute('data-buff-tab') !== this.mainTab,
        onEnd: () => {
          const orderedCustomTabs = Array.from(
            tabContent.querySelectorAll('.buff-management-tab[data-buff-tab]')
          )
            .map(element => element.getAttribute('data-buff-tab'))
            .filter(tab => tab !== this.mainTab);
          this.characterData.buffTabs.splice(
            0,
            this.characterData.buffTabs.length,
            ...orderedCustomTabs
          );
        },
      });
    },
    destroyTabSortable() {
      if (!this.tabSortable) return;
      this.tabSortable.destroy();
      this.tabSortable = null;
    },
    syncCurrentTabBuffs() {
      this.currentTabBuffs = this.buffs.filter(buff => buffUtilities.getBuffTab(buff) === this.editBuffTab);
    },
    applyCurrentTabOrder() {
      this.syncingTabList = true;
      const queue = [...this.currentTabBuffs];
      const result = [];
      this.buffs.forEach(buff => {
        if (buffUtilities.getBuffTab(buff) === this.editBuffTab) {
          const next = queue.shift();
          if (next) result.push(next);
        } else {
          result.push(buff);
        }
      });
      this.characterData.buffs.splice(0, this.characterData.buffs.length, ...result);
      this.$nextTick(() => {
        this.syncingTabList = false;
        this.syncCurrentTabBuffs();
      });
    },
    openAddTabDialog() {
      this.ensureBuffStructure();
      this.tabNameMode = 'add';
      this.tabNameOriginal = null;
      this.tabNameInput = '';
      this.tabNameError = '';
      this.tabNameDialog = true;
    },
    openRenameTabDialog(tab) {
      if (tab === this.mainTab) return;
      this.tabNameMode = 'rename';
      this.tabNameOriginal = tab;
      this.tabNameInput = tab;
      this.tabNameError = '';
      this.tabNameDialog = true;
    },
    closeTabNameDialog() {
      this.tabNameDialog = false;
      this.tabNameError = '';
    },
    saveTabName() {
      const trimmed = this.tabNameInput.trim();
      if (!trimmed) {
        this.tabNameError = 'Enter a tab name.';
        return;
      }
      const original = this.tabNameOriginal;
      const duplicate = trimmed.toLowerCase() === this.mainTab
        || this.buffTabs.some(tab => tab !== original && tab.toLowerCase() === trimmed.toLowerCase());
      if (duplicate) {
        this.tabNameError = 'A tab with that name already exists.';
        return;
      }
      if (this.tabNameMode === 'add') {
        this.buffTabs.push(trimmed);
        this.editBuffTab = trimmed;
      } else {
        const index = this.buffTabs.indexOf(original);
        if (index !== -1) this.$set(this.buffTabs, index, trimmed);
        this.buffs.forEach(buff => {
          if (buffUtilities.getBuffTab(buff) === original) this.$set(buff, 'tab', trimmed);
        });
        if (this.editBuffTab === original) this.editBuffTab = trimmed;
      }
      this.closeTabNameDialog();
    },
    openDeleteTabDialog(tab) {
      if (tab === this.mainTab) return;
      this.tabPendingDeletion = tab;
      this.deleteTabDialog = true;
    },
    confirmDeleteTab() {
      const tab = this.tabPendingDeletion;
      if (!tab || tab === this.mainTab) return;
      this.buffs.forEach(buff => {
        if (buffUtilities.getBuffTab(buff) === tab) this.$set(buff, 'tab', this.mainTab);
      });
      const index = this.buffTabs.indexOf(tab);
      if (index !== -1) this.buffTabs.splice(index, 1);
      if (this.editBuffTab === tab) this.editBuffTab = this.mainTab;
      else this.syncCurrentTabBuffs();
      this.deleteTabDialog = false;
      this.tabPendingDeletion = null;
    },
    onBuffDragStart(evt) {
      this.draggingBuff = this.currentTabBuffs[evt.oldIndex] || null;
      this.dragOverTab = null;
      window.addEventListener('dragover', this.onGlobalDragOver);
      window.addEventListener('mousemove', this.onGlobalDragOver);
    },
    onBuffDragEnd(evt) {
      window.removeEventListener('dragover', this.onGlobalDragOver);
      window.removeEventListener('mousemove', this.onGlobalDragOver);
      const buff = this.draggingBuff;
      let dropTab = this.dragOverTab;
      this.draggingBuff = null;
      this.dragOverTab = null;

      if (!dropTab) {
        const originalEvent = evt.originalEvent || evt;
        const clientX = originalEvent.clientX;
        const clientY = originalEvent.clientY;
        if (clientX != null && clientY != null) {
          const el = document.elementFromPoint(clientX, clientY);
          const tabEl = el && el.closest('[data-buff-tab]');
          dropTab = tabEl && tabEl.getAttribute('data-buff-tab');
        }
      }

      if (buff && dropTab && dropTab !== this.editBuffTab) {
        this.$set(buff, 'tab', dropTab);
        this.syncCurrentTabBuffs();
        return;
      }
      this.applyCurrentTabOrder();
    },
    onGlobalDragOver(event) {
      if (!this.draggingBuff) return;
      const el = document.elementFromPoint(event.clientX, event.clientY);
      const tabEl = el && el.closest('[data-buff-tab]');
      this.dragOverTab = tabEl ? tabEl.getAttribute('data-buff-tab') : null;
    },
    describeEffect(effect) {
      return buffUtilities.describeEffect(effect, this.characterData, this.pointTrackers);
    },
    formatDuration(rounds) {
      return buffUtilities.formatDuration(rounds);
    },
    onBuffToggled(buff) {
      if (!buff.active) return;
      const durationRounds = buffUtilities.getDurationRounds(buff);
      // Zero, negative, "-", blank, and other non-positive durations mean
      // the buff lasts indefinitely.
      this.$set(buff, 'remainingRounds', durationRounds == null ? null : durationRounds);
    },
    commonBuffDurationText(definition) {
      const duration = definition.duration(commonBuffs.normalizeCasterLevel(this.casterLevel));
      return `${duration.value} ${duration.unit}`;
    },
    commonBuffPreview(definition) {
      const built = commonBuffs.buildBuff(definition, this.casterLevel);
      return built.effects
        .map(effect => buffUtilities.describeEffect(effect, this.characterData, this.pointTrackers))
        .join(', ');
    },
    addCommonBuff(definition) {
      this.ensureBuffsArray();
      const buff = commonBuffs.buildBuff(definition, this.casterLevel);
      buff.tab = this.editBuffTab;
      this.buffs.push(buff);
      this.commonAddedMessage = `Added ${definition.name} (CL ${commonBuffs.normalizeCasterLevel(this.casterLevel)})`;
      if (this.commonAddedTimer) clearTimeout(this.commonAddedTimer);
      this.commonAddedTimer = setTimeout(() => { this.commonAddedMessage = ''; }, 2500);
      this.syncCurrentTabBuffs();
    },
    targetOptions(category) {
      return buffUtilities.getTargetOptions(category, this.characterData);
    },
    bonusTypeOptions(category, target) {
      return buffUtilities.getBonusTypeOptions(category, target);
    },
    amountPlaceholder(effect) {
      if (effect && effect.category === 'offense' && effect.target === 'damageDice') {
        return '+1 or -1';
      }
      if (effect && buffUtilities.isDamageExpressionTarget(effect.target)) {
        return '+1d6+2, [{Tracker}+3/0.5]';
      }
      return '+3, [Str/0.5], [{Tracker}+3/0.5]';
    },
    openAddBuff() {
      this.ensureBuffsArray();
      this.editingBuffId = null;
      this.workingBuff = buffUtilities.createBuff(this.editBuffTab);
      this.editError = '';
      this.editDialog = true;
    },
    openEditBuff(buff) {
      this.editingBuffId = buff.id;
      this.workingBuff = JSON.parse(JSON.stringify(buff));
      if (!this.workingBuff.effects || this.workingBuff.effects.length === 0) {
        this.workingBuff.effects = [buffUtilities.createEffect()];
      }
      // Backfill duration fields on buffs created before durations existed
      if (this.workingBuff.durationValue === undefined) this.workingBuff.durationValue = '';
      if (!this.workingBuff.durationUnit) this.workingBuff.durationUnit = 'rounds';
      if (this.workingBuff.remainingRounds === undefined) this.workingBuff.remainingRounds = null;
      if (!this.workingBuff.tab) this.workingBuff.tab = this.editBuffTab || this.mainTab;
      this.editError = '';
      this.editDialog = true;
    },
    removeBuff(buff) {
      const index = this.buffs.indexOf(buff);
      if (index !== -1) this.buffs.splice(index, 1);
      this.syncCurrentTabBuffs();
    },
    addEffect() {
      this.workingBuff.effects.push(buffUtilities.createEffect());
    },
    removeEffect(effect) {
      if (this.workingBuff.effects.length <= 1) return;
      const index = this.workingBuff.effects.indexOf(effect);
      if (index !== -1) this.workingBuff.effects.splice(index, 1);
    },
    onCategoryChange(effect) {
      effect.target = '';
      effect.bonusType = '';
    },
    onTargetChange(effect) {
      if (effect.bonusType && !buffUtilities.isBonusTypeAllowed(effect.category, effect.target, effect.bonusType)) {
        effect.bonusType = '';
      }
    },
    validateWorkingBuff() {
      if (!this.workingBuff.name || !this.workingBuff.name.trim()) {
        return 'Please give the buff a name.';
      }
      const durationValue = String(this.workingBuff.durationValue || '').trim();
      if (durationValue && (!/^\d+$/.test(durationValue) || parseInt(durationValue) <= 0)) {
        return 'Duration must be a positive whole number (or left blank).';
      }
      for (const effect of this.workingBuff.effects) {
        const amountText = String(effect.amount).trim();
        if (!effect.category || !effect.target || !effect.bonusType) {
          return 'Each bonus needs a category, target, and bonus type.';
        }
        if (effect.category === 'offense' && effect.target === 'damageDice') {
          if (!/^[+-]\d+$/.test(amountText)) {
            return 'Dice Size bonuses need a signed whole-number step (e.g. +1 or -2).';
          }
        } else if (buffUtilities.isDamageExpressionTarget(effect.target)) {
          if (
            !buffUtilities.parseDamageExpression(amountText)
            && !buffUtilities.parseAbilityAmount(amountText)
            && !buffUtilities.parsePointTrackerAmount(amountText)
          ) {
            return 'Damage bonuses need dice, a whole number, or a formula (e.g. +1d6+2 or [{Tracker}+3/0.5]).';
          }
        } else if (
          !/^[+-]?\d+$/.test(amountText)
          && !buffUtilities.parseAbilityAmount(amountText)
          && !buffUtilities.parsePointTrackerAmount(amountText)
        ) {
          return 'Each bonus needs a whole number or formula such as [Strength/0.5] or [{Tracker}+3/0.5].';
        }
        if (!buffUtilities.isBonusTypeAllowed(effect.category, effect.target, effect.bonusType)) {
          return 'One of the selected bonus types does not apply to that target.';
        }
      }
      return '';
    },
    confirmEdit() {
      this.editError = this.validateWorkingBuff();
      if (this.editError) return;

      this.ensureBuffsArray();
      this.workingBuff.name = this.workingBuff.name.trim();
      if (!this.workingBuff.tab) this.workingBuff.tab = this.editBuffTab || this.mainTab;
      if (this.editingBuffId === null) {
        this.buffs.push(this.workingBuff);
      } else {
        const index = this.buffs.findIndex(buff => buff.id === this.editingBuffId);
        if (index !== -1) {
          this.buffs.splice(index, 1, this.workingBuff);
        } else {
          this.buffs.push(this.workingBuff);
        }
      }
      this.editDialog = false;
      this.workingBuff = null;
      this.editingBuffId = null;
      this.syncCurrentTabBuffs();
    },
    cancelEdit() {
      this.editDialog = false;
      this.workingBuff = null;
      this.editingBuffId = null;
      this.editError = '';
    },
  },
};
</script>

<style>
/* maneuverTracker.vue forces overflow-y:hidden on every .v-menu__content,
   which prevents long select lists from scrolling. Restore scrolling for
   the buff editor dropdowns. */
.v-menu__content.buff-select-menu {
  overflow-y: auto !important;
  margin-left: 0;
}

.buff-management-card {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.buff-management-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.buff-management-tabs {
  position: sticky;
  top: 0;
  z-index: 2;
  margin: 0 -16px 16px;
  background-color: #DEB887;
  box-shadow: 0 2px 4px rgba(62, 39, 35, 0.25);
}

.buff-management-tab {
  min-width: 88px !important;
  max-width: 220px;
  text-transform: none !important;
  letter-spacing: normal !important;
  color: #3e2723 !important;
}

.buff-management-tab .v-icon {
  color: inherit !important;
}

.buff-tab-sort-ghost {
  opacity: 0.35;
}

.buff-tab-sort-chosen {
  cursor: grabbing !important;
}

.buff-tab-sort-chosen .buff-tab-drag-handle {
  cursor: grabbing !important;
}

.buff-edit-tab--drop {
  outline: 2px dashed #3e2723;
  outline-offset: 2px;
}
</style>
